import {React,useEffect} from "react";
import RedFlagTrigger from "../../components/RedFlag/RedFlagTrigger";
import Users from "../../components/Users/Users";
import TotalIssues from "../../components/Issues/TotalIssues";
import { useRender } from "../../context/renderContext";
import Workshops from "../../components/Workshops/Workshops";
import Features from "../../components/Features/Features";
import KeyInsights from "../../components/KeyInsights/KeyInsights";
import Alert from "../../components/Alert/Alert";
import UsageLimits from "../../components/UsageLimits/UsageLimits";
import TotalUser from "../../components/TotalUsers/employee";
import WorkerStats from "../../components/Onboarding/WorkerStats";
import { Download, Star, Bell, TrendingUp, Leaf } from "lucide-react";
import { useAuth } from "../../context/authContext";
import TestAnalytics from "../Test/TestAnalytics";
import ReferralCodeCard from "../../components/Share";
import { useState } from "react";
import NotificationBell from "../../components/notification";
import { Coffee } from "lucide-react";
import { useTheme } from "../../context/themeContext";
import Take_Break from "../../components/TakeBreakCount";


const CodesToDiffUi = ["WAP", "WAPTEST"];

const TOKENS = {
  dark: {
    isDark: true,
    // Same soft radial-gradient wash as Home — no flat background color of
    // its own, so it inherits the app shell's background and stays visually
    // consistent with the rest of the dashboard instead of looking like a
    // separate, boxed-off page.
    pageWash:
      "bg-[radial-gradient(circle_at_15%_0%,rgba(139,124,246,0.10),transparent_45%),radial-gradient(circle_at_85%_10%,rgba(239,93,168,0.08),transparent_40%)]",
    card: "bg-[#13152c]/90 border-white/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_20px_50px_-20px_rgba(0,0,0,0.65)]",
    textPrimary: "text-white",
    textMuted: "text-slate-400",
    textFaint: "text-slate-500",
    surface: "bg-white/[0.04]",
    surfaceSoft: "bg-white/[0.03]",
    surfaceHover: "hover:bg-white/[0.06]",
    pillBorder: "border-white/10",
    gridStroke: "rgba(255,255,255,0.07)",
    axisTick: "#8790ad",
    tooltipBg: "#1d2144",
    tooltipBorder: "rgba(255,255,255,0.1)",
    overlay: "bg-black/60",
  },
  light: {
    isDark: false,
    pageWash:
      "bg-[radial-gradient(circle_at_10%_-5%,rgba(239,93,168,0.08),transparent_42%),radial-gradient(circle_at_90%_0%,rgba(139,124,246,0.10),transparent_42%)]",
    card: "bg-white/95 border-black/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_20px_44px_-24px_rgba(31,41,74,0.22)]",
    textPrimary: "text-slate-900",
    textMuted: "text-slate-500",
    textFaint: "text-slate-400",
    surface: "bg-black/[0.035]",
    surfaceSoft: "bg-black/[0.03]",
    surfaceHover: "hover:bg-black/[0.045]",
    pillBorder: "border-black/10",
    gridStroke: "rgba(15,23,42,0.08)",
    axisTick: "#7c8299",
    tooltipBg: "#ffffff",
    tooltipBorder: "rgba(15,23,42,0.08)",
    overlay: "bg-slate-900/50",
  },
};


// ---------------------------------------------------------------------------
// Infiheal brand tokens — same identity used across the analytics page.
// This file uses Tailwind's `dark:` variant convention (no theme context
// here), so the brand pieces below are built the same way.
// ---------------------------------------------------------------------------

const BRAND = {
  gradient: "linear-gradient(90deg, #ef5da8 0%, #8b7cf6 100%)",
  teal: "#34d9b8",
  pink: "#ef5da8",
  purple: "#8b7cf6",
};

function InfihealWordmark() {
  return (
    <div className="flex items-center gap-3">
      <div className="flex flex-col leading-none">
        <span className="flex items-baseline text-xl font-extrabold tracking-tight">
          <span className="text-slate-900 dark:text-white">infi</span>
          <span style={{ color: BRAND.teal }}>heal</span>
        </span>
        <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
          HR Dashboard
        </span>
      </div>
    </div>
  );
}

function GradientButton({ children, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_-12px_rgba(139,124,246,0.6)] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
      style={{ background: BRAND.gradient }}
    >
      {Icon ? <Icon size={15} strokeWidth={2.5} /> : null}
      {children}
    </button>
  );
}





const Home = () => {
  const { renderComponent } = useRender();
  const { code } = useAuth();
  console.log(code);
  return (
    <div
      className="w-full h-full overflow-y-auto custom-scrollbar px-3 lg:px-8 py-4 flex flex-col gap-5 text-slate-900 dark:text-white
        bg-[radial-gradient(circle_at_10%_-5%,rgba(239,93,168,0.08),transparent_42%),radial-gradient(circle_at_90%_0%,rgba(139,124,246,0.10),transparent_42%)]
        dark:bg-[radial-gradient(circle_at_15%_0%,rgba(139,124,246,0.10),transparent_45%),radial-gradient(circle_at_85%_10%,rgba(239,93,168,0.08),transparent_40%)]"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div>
          <div
            className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em]"
            style={{ color: BRAND.purple }}
          >
            <TrendingUp size={14} />
            Overview
          </div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
        </div>
        <NotificationBell code={code} />
      </div>

      {code && CodesToDiffUi.includes(code) ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Users />

            <TotalUser />
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <TotalIssues />
            <Users />
            <RedFlagTrigger />
            {code == "AIIMS" ? <Take_Break /> : <TotalUser />}
          </div>
        </>
      )}

      {(code && code === "WAP") || code === "WAPTEST" ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            <div className="flex lg:col-span-3 gap-5">
              <Features name="Features Usage" />
            </div>
            <div className="flex flex-col lg:flex-row lg:col-span-3 gap-5 items-stretch">
              <div className="lg:w-2/5 flex">
                <ReferralCodeCard code={code} />
              </div>

              <div className="lg:w-3/5 flex">
                <WorkerStats />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="w-full">
            {" "}
            <UsageLimits name="Message & Therapy Usage" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-5">
            <div>{renderComponent()}</div>
            <div className="flex flex-col gap-5">
              <Features name="Features Usage" />
              {/* UsageLimits renders nothing (returns null) when code === "WAP"
              or when the backend hides the section, so no extra guard needed here. */}

              <Alert name="Alert Case Issues" />
            </div>
          </div>
        </>
      )}

      {(code && code === "WAP") || code === "WAPTEST" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="lg:col-span-2">
            <TestAnalytics showFooter={false} showBg={false} />
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <ReferralCodeCard code={code} />
            {code == "AIIMS" ? (
              ""
            ) : (
              <div className="col-span-2">
                <WorkerStats />
              </div>
            )}
          </div>
          {code == "AIIMS" ? (
            ""
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <KeyInsights />
              </div>

              <div>
                <Workshops />
              </div>
            </div>
          )}
        </>
      )}

      <div
        className={`mt-2 flex flex-wrap items-center justify-center gap-1 px-4 pb-8 text-center text-xs `}
      >
        <span>Made with</span>
        <span style={{ color: BRAND.pink }}>💗</span>
        <span>
          by Infiheal — private, judgment-free insights for a healthier
          organization.
        </span>
      </div>
    </div>
  );
};

export default Home;
